/**
 * GoalsSummary.js — Dashboard card showing top savings goals progress
 *
 * Shows top 3 in-progress goals sorted by completion percentage (highest first).
 * Each goal displays: emoji, name, mini progress bar, and percentage.
 * Returns null if no active goals (card is hidden, not empty).
 *
 * Props:
 *   goals       — Array of goal objects from getActiveGoals()
 *   onNavigate  — Callback to navigate to Goals page
 */
import React from 'react';
import { formatCurrency } from '../utils/format';
import theme from '../utils/theme';

export default function GoalsSummary({ goals = [], onNavigate }) {
  if (!goals || goals.length === 0) return null;

  // Filter to in-progress goals (not yet complete), sort by completion % desc
  const inProgress = goals
    .map(g => ({
      ...g,
      pct: g.target_amount > 0 ? Math.min(100, (g.current_amount / g.target_amount) * 100) : 0,
    }))
    .sort((a, b) => b.pct - a.pct)
    .slice(0, 3);

  if (inProgress.length === 0) return null;

  const totalSaved = goals.reduce((s, g) => s + (g.current_amount || 0), 0);
  const totalTarget = goals.reduce((s, g) => s + (g.target_amount || 0), 0);
  const remaining = goals.length - inProgress.length;

  return (
    <div className="card" onClick={onNavigate} style={{ cursor: 'pointer' }}>
      <div className="goal-summary-header">
        <h3>🎯 Savings Goals</h3>
        <span style={{ fontSize: '0.75rem', color: theme.textMuted }}>
          {formatCurrency(totalSaved)} of {formatCurrency(totalTarget)}
        </span>
      </div>

      {inProgress.map(goal => (
        <div key={goal.id} className="goal-summary-row">
          <span className="goal-summary-emoji">{goal.emoji || '🎯'}</span>
          <div className="goal-summary-info">
            <div className="goal-summary-name">{goal.name}</div>
            <div className="goal-progress-bar goal-progress-bar--mini">
              <div
                className={`goal-progress-fill ${goal.pct >= 100 ? 'goal-progress-fill--complete' : 'goal-progress-fill--on-track'}`}
                style={{ width: `${goal.pct}%` }}
              />
            </div>
          </div>
          <span className="goal-summary-pct">{Math.round(goal.pct)}%</span>
        </div>
      ))}

      {remaining > 0 && (
        <div style={{ textAlign: 'center', paddingTop: '0.35rem', fontSize: '0.75rem', color: theme.textMuted }}>
          +{remaining} more goal{remaining !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
}
